import { Page } from "shared/ui";
import {
  TaskHubItem,
  useCreateTaskMutation,
  useDeleteTaskMutation,
  useGetTasksQuery,
  useUpdateTaskMutation,
} from "shared/api/task-hub";
import React, { useRef } from "react";
import {
  useConfirmDeleteModal,
  usePagination,
  useTableSorting,
} from "shared/hooks";
import { useTranslation } from "react-i18next";
import {
  MachineItem,
  useGetAllCompaniesQuery,
  useGetAllMachinesQuery,
  useGetUsersQuery,
  UserItem,
} from "shared/api";
import { useTaskHubState } from "shared/hooks/useTaskHubState";
import { useTaskHubFilters } from "shared/hooks/useTaskHubFilters";
import { useTaskHubColumns } from "../../../../../shared/hooks/useTaskHubColumns";
import { TaskHubMain } from "./TaskHubMain";

const TaskHubUserSettings = () => {
  const { t } = useTranslation();
  const pagination = usePagination(10);
  const state = useTaskHubState();
  const filters = useTaskHubFilters();

  const { setCurrentPage, pageSize, currentPage, offset, limit } = pagination;
  const { ordering } = useTableSorting();

  const { data: allUsers = [] } = useGetUsersQuery({}) as { data?: UserItem[] };
  const transformedUsers = allUsers?.map((user) => ({
    label: user.username,
    value: user.id,
  }));

  const taskScheduleRef = useRef<{ refetchExecutions: () => void }>(null);

  const extractIds = (arr: any[], key: string) =>
    arr?.map((item) => item[key]?.id ?? item.id) || [];

  const { data: machinesData } = useGetAllMachinesQuery({}) as {
    data: MachineItem[] | undefined;
  };

  const { data, isLoading, refetch } = useGetTasksQuery({
    offset,
    limit,
    ordering,
    name__icontains: filters.debouncedTaskNameValue,
    periodicity: filters.debouncedPeriodicityValue,
    start_periodicity__gte: filters.startDateFilters.dateGreater ?? undefined,
    start_periodicity__lte: filters.startDateFilters.dateLesser ?? undefined,
    priority__gte: filters.priorityFilters.minValue ?? undefined,
    priority__lte: filters.priorityFilters.maxValue ?? undefined,
    enabled: filters.enabledFilter,
    users__user__in: filters.userIds.length > 0 ? filters.userIds : undefined,
    trigger__in:
      filters.triggerInFilter.length > 0 ? filters.triggerInFilter : undefined,
    expand: "machines,product_cards,files,users,companies",
  });

  const { data: suppliersCompanies } = useGetAllCompaniesQuery({
    is_supplier: true,
  });

  const suppliersCompaniesFormatted = suppliersCompanies?.map((s) => ({
    label: s.name,
    value: s.id,
  }));

  const [deleteTask] = useDeleteTaskMutation();
  const [createTask] = useCreateTaskMutation();
  const [updateTask] = useUpdateTaskMutation();

  const handleDuplicate = async (task: TaskHubItem) => {
    try {
      const duplicated = {
        ...Object.fromEntries(
          Object.entries(task).filter(
            ([key]) =>
              key !== "icon" && key !== "time" && key !== "end_periodicity"
          )
        ),
        name: `${task.name} (copy)`,
        machines: extractIds(task.machines, "machine"),
        product_cards: extractIds(task.product_cards, "product_card"),
        users: extractIds(task.users, "user"),
        companies: extractIds(task.companies, "companies"),
      };

      await createTask(duplicated).unwrap();
      refetch();
    } catch (err) {
      /* eslint-disable no-console */
      console.error("Failed to duplicate task", err);
    }
  };

  const deletTaskHandler = async (id: number) => {
    try {
      await deleteTask(id).unwrap();
      refetch();
    } catch (err) {
      /* eslint-disable no-console */
      console.error("Failed to delete task", err);
    }
  };

  const handleToggleStatus = async (task: TaskHubItem) => {
    try {
      await updateTask({
        id: task.id,
        name: task.name,
        periodicity: task.periodicity,
        start_periodicity: task.start_periodicity,
        priority: task.priority,
        enabled: !task.enabled,
        users: extractIds(task.users, "user"),
        user__full_name: task.user__full_name,
        description: task.description,
        machines: extractIds(task.machines, "machine"),
        product_cards: extractIds(task.product_cards, "product_card"),
      }).unwrap();
      refetch();
    } catch (err) {
      /* eslint-disable no-console */
      console.error("Failed to toggle status", err);
    }
  };

  const confirmDelete = useConfirmDeleteModal(
    t("Are you sure you want to delete this task?"),
    (record: number) => deletTaskHandler(record)
  );

  const columns = useTaskHubColumns({
    onEdit: (task: number | null) => {
      state.setEditingTask(task);
      state.setIsEditModalOpen(true);
    },
    onDelete: (id: number | null) => {
      if (id !== null) confirmDelete(id);
    },
    onDuplicate: handleDuplicate,
    transformedUsers,
    handleToggleStatus,
    handleTaskNameValue: filters.handleTaskNameValue,
    taskNameValue: filters.taskNameValue,
    handlePeriodicityValue: filters.handlePeriodicityValue,
    periodicityValue: filters.periodicityValue,
    enabledFilter: filters.enabledFilter,
    setEnabledFilter: filters.setEnabledFilter,
    handleStartDateGreater: filters.startDateFilters.handleDateGreater,
    handleStartDateLesser: filters.startDateFilters.handleDateLesser,
    startDateGreater: filters.startDateFilters.dateGreater,
    startDateLesser: filters.startDateFilters.dateLesser,
    isStartFilterActive: filters.startDateFilters.isDataFilterActive,
    userIds: filters.userIds,
    priorityFilters: filters.priorityFilters,
    setUserIds: filters.setUserIds,
    triggerInFilter: filters.triggerInFilter,
    setTriggerInFilter: filters.setTriggerInFilter,
    refetch,
  });

  return (
    <Page
      title={t("taskHub.taskHub")}
      buttonTitle={t("taskHub.buttons.addTask") || ""}
      onClick={() => state.setIsAddModalOpen(true)}
      isVisibleClearFiltersButton={filters.isVisibleClearFiltersButton}
      clearAllFilters={filters.clearAllFilters}
    >
      <TaskHubMain
        data={data}
        isLoading={isLoading}
        currentPage={currentPage}
        pageSize={pageSize}
        setCurrentPage={setCurrentPage}
        setPageSize={pagination.setPageSize!}
        columns={columns}
        suppliersCompaniesFormatted={suppliersCompaniesFormatted}
        machinesData={machinesData}
        allUsers={allUsers}
        transformedUsers={transformedUsers}
        modalState={{ ...state }}
        refetch={refetch}
        taskScheduleRef={taskScheduleRef}
      />
    </Page>
  );
};

export { TaskHubUserSettings };
